import type { ResolveBrandingResponse } from "@/client/types.gen";
import { getPortalSlug } from "@/lib/portal";
import { Loader2 } from "lucide-react";
import React, { createContext, useContext, useEffect, useState } from "react";

interface PortalContextType {
  /** True when the current host is a branded vault portal subdomain. */
  isPortal: boolean;
  /** Portal slug parsed from the host, or null on the apex/app host. */
  slug: string | null;
  /** Resolved org id for the portal, once branding loads. */
  orgId: string | null;
  /** Resolved branding (org name + theme), or null. */
  branding: ResolveBrandingResponse | null;
  /** True while branding is being resolved for a portal host. */
  loading: boolean;
}

const PortalContext = createContext<PortalContextType | undefined>(undefined);

export const usePortal = () => {
  const ctx = useContext(PortalContext);
  if (!ctx) throw new Error("usePortal must be used within a PortalProvider");
  return ctx;
};

/** Applies the org's theme to the document (colours, font, favicon). */
function applyTheme(branding: ResolveBrandingResponse | null) {
  const root = document.documentElement;
  const theme = branding?.theme;
  if (!theme) return;

  if (theme.primary_color)
    root.style.setProperty("--primary", theme.primary_color);
  if (theme.secondary_color)
    root.style.setProperty("--secondary", theme.secondary_color);
  if (theme.typography) root.style.setProperty("--font-sans", theme.typography);

  if (theme.favicon_url) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = theme.favicon_url;
  }

  if (branding?.name) {
    document.title = branding.name;
  }
}

/** Resolve branding from the backend API. */
async function resolvePortalBranding(
  hostname: string
): Promise<ResolveBrandingResponse | null> {
  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:8000";
  const res = await fetch(
    `${apiUrl}/branding/resolve?host=${encodeURIComponent(hostname)}`
  );
  if (!res.ok) {
    if (res.status === 404) return null;
    throw new Error(`Failed to resolve branding: ${res.statusText}`);
  }
  return res.json();
}

export const PortalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const slug = getPortalSlug();
  const isPortal = slug !== null;

  const [branding, setBranding] = useState<ResolveBrandingResponse | null>(
    null
  );
  const [loading, setLoading] = useState(isPortal);

  useEffect(() => {
    if (!isPortal) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await resolvePortalBranding(window.location.hostname);
        if (cancelled) return;
        setBranding(res);
        applyTheme(res);
      } catch (e) {
        // Unknown slug / no branding: fall through to the default unbranded portal.
        console.error("Failed to resolve portal branding", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPortal]);

  if (isPortal && loading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <PortalContext.Provider
      value={{
        isPortal,
        slug,
        orgId: branding?.org_id ?? null,
        branding,
        loading,
      }}
    >
      {children}
    </PortalContext.Provider>
  );
};
